/* istanbul ignore file */
import {
    Collapse,
    TableBody,
    TableContainer,
    TableFooter,
    TableRow,
    useMediaQuery,
} from '@mui/material';
import { useMemo, useState } from 'react';

import {
    PinoProductTitle,
    BenefitsHeadings,
    productTitle,
    getShortenedBenefitsHeadings,
    getShortenedStudentBenefitsHeadings,
    getBafShortenedBenefitsHeadings,
} from '@tgg/common-types';
import {
    dispatchEvent,
    BenefitsOverlayPayload,
    DataEventNames,
    EventKey,
    sendAnalyticsDataEvent,
} from '@tgg/services';
import { palette, theme } from '@tgg/ui';
import { decimalToPoundsAndPence } from '@tgg/util';

import { getPinoColor } from '../../utils/getPinoColors';
import { Icon } from '../Icon';
import { PinoIconButton } from '../PinoIconButton';
import { PinoOverlay } from '../PinoOverlay';
import {
    getAdditionalBenefitButton,
    getHiddenBenefitCount,
    getHiddenBenefits,
    getInitialSelectedProduct,
    getVisibleBenefits,
    getKickerRibbonTitle,
    mapPinoProductsWithDetails,
    shouldDisableProduct,
} from '../PinoTable/PinoTable.helpers';
import {
    PinoProductWithDetails,
    PinoTableProperties,
} from '../PinoTable/PinoTable.types';
import { getPinoProductBenefitsMBTV3 } from '../PinoTable/pino-table.constants';
import {
    StyledExpanderPinoButton,
    StyledKickerOfferRibbon,
    StyledKickerOfferRibbonText,
    StyledProductTitle,
    StyledSVGOutline,
    useTableCollapseOverrides,
} from '../PinoTableMBTOptimization/PinoTableMBTOptimization.styled';

import {
    StyledBoldSpanText,
    StyledCaption,
    StyledMoText,
    StyledButtonWrapperForIcon,
    StyledProductHeader,
    StyledProductHeaderPrices,
    StyledProductHeaderJoiningFee,
    StyledTable,
    StyledTableCell,
    StyledTableFooterCell,
    StylesTableRow,
} from './PinoTableMBTV3.styled';

export const PinoTableMBTV3 = ({
    pinoMonthlyProducts,
    onSelectedProduct,
    tableCaption,
    disabledProduct,
    gymName,
    isOpen24Hours,
    isKickerOffersRibbonEnabled,
    isStudentPinoTable = false,
    isDayPassPinoTable = false,
    showFooter = true,
    isProductSelectable = true,
    isBenefitsShortened = false,
    isGuestPassEnabled = false,
    isFreeFreezeNoticeVisible = false,
    isMBTOptimization = false,
    isMBTOptimizationV2 = false,
    isMBTOptimizationV3 = false,
    offPeakTimes,
    openingHours,
}: PinoTableProperties) => {
    const isPinoMembershipTable = !isStudentPinoTable && !isDayPassPinoTable;
    const isDesktop = useMediaQuery(theme.breakpoints.up('desktop'));

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<PinoProductTitle>(
        () =>
            getInitialSelectedProduct(
                disabledProduct,
                isStudentPinoTable,
                isDayPassPinoTable,
            ),
    );
    const [hoveredProduct, setHoveredProduct] =
        useState<PinoProductTitle | null>();
    const overridesCollapseClasses = useTableCollapseOverrides();

    const productBenefits = useMemo(
        () =>
            getVisibleBenefits(
                isMBTOptimization,
                false,
                isMBTOptimizationV2,
                isStudentPinoTable,
                isDayPassPinoTable,
                isBenefitsShortened,
                isGuestPassEnabled,
                isMBTOptimizationV3,
            ),
        [
            isMBTOptimization,
            isStudentPinoTable,
            isDayPassPinoTable,
            isBenefitsShortened,
            isGuestPassEnabled,
            isMBTOptimizationV3,
            isMBTOptimizationV2,
        ],
    );

    const hiddenSelectBenefits = useMemo(
        () => getAdditionalBenefitButton(isMBTOptimization),
        [isMBTOptimization],
    );
    const hiddenAdditionalBenefits = useMemo(
        () => getHiddenBenefits(isMBTOptimization),
        [isMBTOptimization],
    );

    const productsWithDetails = mapPinoProductsWithDetails(
        pinoMonthlyProducts,
        getPinoProductBenefitsMBTV3(offPeakTimes, openingHours, isOpen24Hours),
    );

    const isOffPeakProductExists = productsWithDetails.some(product =>
        product.membershipType.includes('OffPeak'),
    );

    const selectProduct = (product: PinoProductWithDetails) => {
        if (isProductSelectable) {
            const { title } = product;

            if (title !== selectedProduct) {
                setSelectedProduct(title);
            }

            if (onSelectedProduct) {
                onSelectedProduct(product);
            }

            sendAnalyticsDataEvent({
                eventName: DataEventNames.PRODUCT_SELECT,
                eventData: product,
                eventGymName: gymName,
            });
        }
    };

    const handleClose = () => setIsModalOpen(false);
    const handleOpen = () => setIsModalOpen(true);

    const handleMouseEnter = (title: PinoProductTitle) => {
        // Only set hover state if on desktop or when mouse if on a different product to the selected
        if (!isDesktop || title === selectedProduct) {
            return;
        }
        setHoveredProduct(title);
    };

    const handleMouseLeave = (title: PinoProductTitle) => {
        // Only set hover state if on desktop or when mouse if on a different product to the selected
        if (!isDesktop || title === selectedProduct) {
            return;
        }
        setHoveredProduct(null);
    };

    let pinoTable: 'membership' | 'student' | 'daypass';

    if (isStudentPinoTable) {
        pinoTable = 'student';
    } else if (isDayPassPinoTable) {
        pinoTable = 'daypass';
    } else {
        pinoTable = 'membership';
    }

    let benefitsOverlayTitle: string;
    switch (pinoTable) {
        case 'student': {
            benefitsOverlayTitle = 'student membership benefits';
            break;
        }
        case 'daypass': {
            benefitsOverlayTitle = 'your day pass includes';
            break;
        }
        default: {
            benefitsOverlayTitle = 'your membership benefits';
            break;
        }
    }

    return (
        <>
            <TableContainer data-testid="pino-table-v3">
                <StyledButtonWrapperForIcon
                    type="button"
                    onClick={() => {
                        handleOpen();
                        dispatchEvent<BenefitsOverlayPayload>(
                            EventKey.SELECT_CONTENT,
                            {
                                content_id: benefitsOverlayTitle,
                                content_type: 'chevron right',
                                cta_text: isBenefitsShortened
                                    ? 'More about benefits'
                                    : 'Explore benefits',
                            },
                        );
                    }}
                    aria-label="Benefits information icon"
                    $isMBTOptimization={true}
                >
                    <Icon name="information" size={18} />
                    {isBenefitsShortened &&
                        !isMBTOptimization &&
                        'More about benefits'}
                    {isMBTOptimization && <p>Explore benefits</p>}
                </StyledButtonWrapperForIcon>
                <StyledTable
                    aria-label="PINO table"
                    $isClippedWidth={isStudentPinoTable || isDayPassPinoTable}
                >
                    <StyledCaption>{tableCaption}</StyledCaption>
                    <TableBody>
                        <TableRow>
                            {productsWithDetails.map(product => {
                                const {
                                    id,
                                    price,
                                    title,
                                    joiningFee,
                                    variant,
                                } = product;
                                const isProductDisabled = shouldDisableProduct(
                                    product,
                                    disabledProduct,
                                );

                                const isKickerOfferRibbonShouldBeDisplayed =
                                    isKickerOffersRibbonEnabled &&
                                    !!price.kickerPriceDuration;

                                return (
                                    <StyledProductHeader
                                        theme={theme}
                                        $isMBTOptimization
                                        $selectedProduct={
                                            title === selectedProduct
                                                ? title
                                                : undefined
                                        }
                                        $hoveredProduct={
                                            title === hoveredProduct &&
                                            title !== selectedProduct
                                                ? title
                                                : undefined
                                        }
                                        onClick={() => {
                                            selectProduct(product);
                                            setSelectedProduct(title);
                                        }}
                                        onMouseEnter={() =>
                                            handleMouseEnter(title)
                                        }
                                        onMouseLeave={() =>
                                            handleMouseLeave(title)
                                        }
                                        key={id}
                                        component="th"
                                        scope="col"
                                        align="center"
                                        role="columnheader"
                                        $isKickerOfferRibbon={
                                            isKickerOfferRibbonShouldBeDisplayed
                                        }
                                        title={title}
                                        disabled={isProductDisabled}
                                        $isStudentPinoTable={isStudentPinoTable}
                                    >
                                        {isKickerOfferRibbonShouldBeDisplayed && (
                                            <StyledKickerOfferRibbon>
                                                <StyledKickerOfferRibbonText>
                                                    {getKickerRibbonTitle(
                                                        price.kickerPriceDuration,
                                                    )}
                                                </StyledKickerOfferRibbonText>
                                            </StyledKickerOfferRibbon>
                                        )}

                                        <Icon
                                            name={title}
                                            color={palette.common.white}
                                            size={26}
                                        />
                                        <StyledProductTitle
                                            $isStudentPinoTable={
                                                isStudentPinoTable ||
                                                isDayPassPinoTable
                                            }
                                        >
                                            {productTitle[variant] || title}
                                        </StyledProductTitle>
                                        {!isDayPassPinoTable && (
                                            <StyledProductHeaderPrices>
                                                <StyledBoldSpanText>
                                                    <span>from</span>
                                                    {decimalToPoundsAndPence(
                                                        price.value,
                                                    )}
                                                    {!isStudentPinoTable && (
                                                        <StyledMoText>
                                                            /mo
                                                        </StyledMoText>
                                                    )}
                                                </StyledBoldSpanText>
                                                <StyledProductHeaderJoiningFee>
                                                    +{' '}
                                                    {decimalToPoundsAndPence(
                                                        joiningFee || 0,
                                                        false,
                                                    )}{' '}
                                                    joining fee
                                                </StyledProductHeaderJoiningFee>
                                            </StyledProductHeaderPrices>
                                        )}
                                    </StyledProductHeader>
                                );
                            })}
                        </TableRow>
                        {productBenefits.map((heading, index) => (
                            <StylesTableRow
                                key={heading}
                                $showFooter={showFooter}
                                $isFirstRow={index === 0}
                                $isShowLastRowAfter={
                                    index === productBenefits.length - 1 &&
                                    (isStudentPinoTable ||
                                        isDayPassPinoTable) &&
                                    isMBTOptimizationV3
                                }
                            >
                                {productsWithDetails.map(row => {
                                    const { id, includedBenefits, title } = row;

                                    const shortenedBenefits = isStudentPinoTable
                                        ? getShortenedStudentBenefitsHeadings(
                                              isMBTOptimization,
                                          )
                                        : /* istanbul ignore next */
                                          isGuestPassEnabled
                                          ? getShortenedBenefitsHeadings(
                                                isMBTOptimization,
                                                false,
                                                false,
                                                true,
                                            )
                                          : getBafShortenedBenefitsHeadings(
                                                isMBTOptimization,
                                                false,
                                                false,
                                                true,
                                            );
                                    const headingForBenefits =
                                        isBenefitsShortened
                                            ? (shortenedBenefits[
                                                  heading
                                              ] as BenefitsHeadings)
                                            : (heading as BenefitsHeadings);

                                    if (!includedBenefits[headingForBenefits]) {
                                        return null;
                                    }

                                    if (
                                        !includedBenefits[headingForBenefits]
                                            .display
                                    ) {
                                        return null;
                                    }

                                    const isProductDisabled =
                                        shouldDisableProduct(
                                            row,
                                            disabledProduct,
                                        );

                                    return (
                                        <StyledTableCell
                                            theme={theme}
                                            $isMBTOptimization={true}
                                            data-ab-test={
                                                title === hoveredProduct &&
                                                title !== selectedProduct
                                                    ? `${title}-hovered`
                                                    : title === selectedProduct
                                                      ? `${title}-selected`
                                                      : undefined
                                            }
                                            $selectedProduct={
                                                title === selectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            $hoveredProduct={
                                                title === hoveredProduct &&
                                                title !== selectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            $isDesktop={isDesktop}
                                            onMouseEnter={() =>
                                                handleMouseEnter(title)
                                            }
                                            onMouseLeave={() =>
                                                handleMouseLeave(title)
                                            }
                                            onClick={() => {
                                                selectProduct(row);
                                                setSelectedProduct(title);
                                            }}
                                            key={id}
                                            align="center"
                                            rowSpan={
                                                includedBenefits[
                                                    headingForBenefits
                                                ].rowSpan
                                            }
                                            disabled={isProductDisabled}
                                            $isStudentPinoTable={
                                                isStudentPinoTable
                                            }
                                            $isPinoMembershipTable={
                                                isPinoMembershipTable
                                            }
                                        >
                                            {
                                                includedBenefits[
                                                    headingForBenefits
                                                ].display
                                            }
                                        </StyledTableCell>
                                    );
                                })}
                            </StylesTableRow>
                        ))}
                        <Collapse
                            in={open}
                            timeout="auto"
                            unmountOnExit
                            classes={{
                                wrapper: overridesCollapseClasses.wrapper,
                                root: overridesCollapseClasses.root,
                                wrapperInner:
                                    overridesCollapseClasses.wrapperInner,
                            }}
                        >
                            {hiddenAdditionalBenefits.map(heading => (
                                <TableRow key={heading}>
                                    {productsWithDetails.map(row => {
                                        if (
                                            !row.includedBenefits[
                                                heading as BenefitsHeadings
                                            ]
                                        ) {
                                            return null;
                                        }

                                        const { id, includedBenefits, title } =
                                            row;
                                        const isProductDisabled =
                                            shouldDisableProduct(
                                                row,
                                                disabledProduct,
                                            );

                                        return (
                                            <StyledTableCell
                                                theme={theme}
                                                $selectedProduct={
                                                    title === selectedProduct
                                                        ? title
                                                        : undefined
                                                }
                                                $hoveredProduct={
                                                    title === hoveredProduct &&
                                                    title !== selectedProduct
                                                        ? title
                                                        : undefined
                                                }
                                                onMouseEnter={() =>
                                                    handleMouseEnter(title)
                                                }
                                                onMouseLeave={() =>
                                                    handleMouseLeave(title)
                                                }
                                                onClick={() => {
                                                    selectProduct(row);
                                                    setSelectedProduct(title);
                                                }}
                                                key={id}
                                                align="center"
                                                rowSpan={
                                                    includedBenefits[
                                                        heading as BenefitsHeadings
                                                    ].rowSpan
                                                }
                                                disabled={isProductDisabled}
                                            >
                                                {
                                                    includedBenefits[
                                                        heading as BenefitsHeadings
                                                    ].display
                                                }
                                            </StyledTableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </Collapse>
                        {hiddenSelectBenefits.map(heading => (
                            <TableRow key={heading}>
                                {productsWithDetails.map(row => {
                                    const { id, includedBenefits, title } = row;
                                    const isProductDisabled =
                                        shouldDisableProduct(
                                            row,
                                            disabledProduct,
                                        );

                                    if (
                                        !includedBenefits[
                                            heading as BenefitsHeadings
                                        ]
                                    ) {
                                        return null;
                                    }

                                    if (
                                        heading === 'Additional benefits' &&
                                        title === 'ultimate'
                                    ) {
                                        return (
                                            <StyledTableCell
                                                theme={theme}
                                                $selectedProduct={
                                                    title === selectedProduct
                                                        ? title
                                                        : undefined
                                                }
                                                $hoveredProduct={
                                                    title === hoveredProduct &&
                                                    title !== selectedProduct
                                                        ? title
                                                        : undefined
                                                }
                                                onMouseEnter={() =>
                                                    handleMouseEnter(title)
                                                }
                                                onMouseLeave={() =>
                                                    handleMouseLeave(title)
                                                }
                                                key={id}
                                                align="center"
                                                rowSpan={
                                                    includedBenefits[heading]
                                                        .rowSpan
                                                }
                                                disabled={isProductDisabled}
                                            >
                                                <StyledExpanderPinoButton
                                                    fullWidth
                                                    onClick={() =>
                                                        setOpen(
                                                            currentValue =>
                                                                !currentValue,
                                                        )
                                                    }
                                                >
                                                    {open ? (
                                                        <>
                                                            <StyledSVGOutline
                                                                $shouldRotate={
                                                                    open
                                                                }
                                                            >
                                                                <Icon
                                                                    name="plus"
                                                                    color={
                                                                        palette
                                                                            .common
                                                                            .blue
                                                                    }
                                                                    size={18}
                                                                    accessibleTitle="close button for additional benefits expander"
                                                                />
                                                            </StyledSVGOutline>
                                                            show less
                                                        </>
                                                    ) : (
                                                        <>
                                                            <StyledSVGOutline
                                                                $shouldRotate={
                                                                    false
                                                                }
                                                            >
                                                                {
                                                                    includedBenefits[
                                                                        heading
                                                                    ].display
                                                                }
                                                            </StyledSVGOutline>
                                                            {getHiddenBenefitCount(
                                                                isMBTOptimization,
                                                            )}{' '}
                                                            more
                                                        </>
                                                    )}
                                                </StyledExpanderPinoButton>
                                            </StyledTableCell>
                                        );
                                    }

                                    return (
                                        <StyledTableCell
                                            theme={theme}
                                            $selectedProduct={
                                                title === selectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            $hoveredProduct={
                                                title === hoveredProduct &&
                                                title !== selectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            onMouseEnter={() =>
                                                handleMouseEnter(title)
                                            }
                                            onMouseLeave={() =>
                                                handleMouseLeave(title)
                                            }
                                            onClick={() => {
                                                selectProduct(row);
                                                setSelectedProduct(title);
                                            }}
                                            key={id}
                                            align="center"
                                            rowSpan={
                                                includedBenefits[
                                                    heading as BenefitsHeadings
                                                ].rowSpan
                                            }
                                            disabled={isProductDisabled}
                                        >
                                            {
                                                includedBenefits[
                                                    heading as BenefitsHeadings
                                                ].display
                                            }
                                        </StyledTableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                    {showFooter && (
                        <TableFooter>
                            <TableRow>
                                {productsWithDetails.map(product => {
                                    const { title, id, price } = product;
                                    const isTitleSelectedProduct =
                                        title === selectedProduct;
                                    const iconColor = isTitleSelectedProduct
                                        ? getPinoColor({
                                              productTitle: title,
                                              isMBTOptimization,
                                          }).primary
                                        : palette.grey[50];
                                    const isProductDisabled =
                                        shouldDisableProduct(
                                            product,
                                            disabledProduct,
                                        );

                                    return (
                                        <StyledTableFooterCell
                                            theme={theme}
                                            isOffPeakProductExists={
                                                isOffPeakProductExists
                                            }
                                            $isMBTOptimization={true}
                                            $selectedProduct={
                                                isTitleSelectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            data-ab-test={
                                                title === hoveredProduct &&
                                                title !== selectedProduct
                                                    ? `${title}-hovered`
                                                    : isTitleSelectedProduct
                                                      ? `${title}-selected`
                                                      : undefined
                                            }
                                            $hoveredProduct={
                                                title === hoveredProduct &&
                                                title !== selectedProduct
                                                    ? title
                                                    : undefined
                                            }
                                            onMouseEnter={() =>
                                                handleMouseEnter(title)
                                            }
                                            onMouseLeave={() =>
                                                handleMouseLeave(title)
                                            }
                                            onClick={() => {
                                                selectProduct(product);
                                                setSelectedProduct(title);
                                            }}
                                            key={id}
                                            align="center"
                                            variant="footer"
                                            disabled={isProductDisabled}
                                        >
                                            <PinoIconButton
                                                aria-label={`${title} from ${decimalToPoundsAndPence(
                                                    price.value,
                                                )} a month`}
                                                size="small"
                                                data-testid={`select-${title}-plan`}
                                                selected={
                                                    isTitleSelectedProduct
                                                }
                                                disabled={isProductDisabled}
                                            >
                                                <Icon
                                                    name="tick"
                                                    color={iconColor}
                                                    size={16}
                                                />
                                            </PinoIconButton>
                                        </StyledTableFooterCell>
                                    );
                                })}
                            </TableRow>
                        </TableFooter>
                    )}
                </StyledTable>
            </TableContainer>
            <PinoOverlay
                title={benefitsOverlayTitle}
                open={isModalOpen}
                handleClose={handleClose}
                pinoTable={pinoTable}
                isOpen24Hours={isOpen24Hours}
                isFreeFreezeAlternativeText={isFreeFreezeNoticeVisible}
                isGuestPassEnabled={isGuestPassEnabled}
            />
        </>
    );
};
